/**
 * PDF Renderer - Core PDF loading and rendering logic
 *
 * Handles:
 * - Loading PDF documents from URLs
 * - Rendering pages to canvas elements
 * - Managing page dimensions and scale
 * - Device pixel ratio for sharp rendering
 */

import { useEffect, useRef, useState, useCallback } from 'react';
import * as pdfjsLib from 'pdfjs-dist';
import { PDFPageInfo } from '../types';

export interface UsePDFRendererOptions {
  pdfUrl: string;
  zoom?: number;
  onLoad?: () => void;
  onError?: (error: string) => void;
}

export interface UsePDFRendererResult {
  pdfDocument: pdfjsLib.PDFDocumentProxy | null;
  pagesInfo: PDFPageInfo[];
  totalPages: number;
  isLoading: boolean;
  error: string | null;
  renderPage: (pageNumber: number, canvasId: string) => Promise<void>;
}

export const usePDFRenderer = ({
  pdfUrl,
  zoom = 1,
  onLoad,
  onError,
}: UsePDFRendererOptions): UsePDFRendererResult => {
  const [pdfDocument, setPdfDocument] = useState<pdfjsLib.PDFDocumentProxy | null>(null);
  const [pagesInfo, setPagesInfo] = useState<PDFPageInfo[]>([]);
  const [totalPages, setTotalPages] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const pdfDocumentRef = useRef<pdfjsLib.PDFDocumentProxy | null>(null);
  const isMountedRef = useRef(true);
  const renderTasksRef = useRef<Map<string, any>>(new Map()); // Track ongoing render tasks by canvasId

  /**
   * Load PDF from URL and get page information
   */
  useEffect(() => {
    isMountedRef.current = true;

    const loadPDF = async () => {
      try {
        setIsLoading(true);
        setError(null);

        // Cancel all ongoing render tasks when loading new PDF
        renderTasksRef.current.forEach((task) => {
          try {
            task.cancel();
          } catch (err) {
            // Ignore cancellation errors
          }
        });
        renderTasksRef.current.clear();

        const loadingTask = pdfjsLib.getDocument({
          url: pdfUrl,
          standardFontDataUrl: '/standard_fonts/',
        });
        const pdf = await loadingTask.promise;

        if (!isMountedRef.current) return;

        pdfDocumentRef.current = pdf;
        setPdfDocument(pdf);
        setTotalPages(pdf.numPages);

        // Initial load - calculate base page info
        setIsLoading(false);
        onLoad?.();
      } catch (err: any) {
        if (isMountedRef.current) {
          const errorMessage = `Failed to load PDF: ${err?.message || 'Unknown error'}`;
          setError(errorMessage);
          setIsLoading(false);
          onError?.(errorMessage);
        }
      }
    };

    loadPDF();

    return () => {
      isMountedRef.current = false;
      // Cancel all ongoing renders on unmount
      renderTasksRef.current.forEach((task) => {
        try {
          task.cancel();
        } catch (err) {
          // Ignore cancellation errors
        }
      });
      renderTasksRef.current.clear();
    };
  }, [pdfUrl, onLoad, onError]);

  /**
   * Recalculate page dimensions when zoom changes
   * Standard approach: zoom 1.0 = 100%, pages centered, scroll naturally
   */
  useEffect(() => {
    if (!pdfDocument) return;

    const calculatePageDimensions = async () => {
      // Standard scale: 1.0 = 100% zoom (72 DPI)
      // zoom parameter directly controls display size (0.5 = 50%, 2.0 = 200%, etc.)
      const displayScale = zoom;

      const pages: PDFPageInfo[] = [];
      for (let i = 1; i <= pdfDocument.numPages; i++) {
        const page = await pdfDocument.getPage(i);
        // Respect page rotation from PDF metadata to prevent flipped/upside-down rendering
        const viewport = page.getViewport({ scale: displayScale, rotation: page.rotate });
        pages.push({
          pageNumber: i,
          width: viewport.width,
          height: viewport.height,
          scale: displayScale,
        });
      }

      if (isMountedRef.current) {
        setPagesInfo(pages);
      }
    };

    calculatePageDimensions();
  }, [pdfDocument, zoom]);

  /**
   * Render a single page to a canvas element with textLayer
   * Standard PDF.js rendering with high-DPI support + text layer for selection
   */
  const renderPage = useCallback(async (pageNumber: number, canvasId: string) => {
    if (!pdfDocument) {
      return;
    }

    try {
      const page = await pdfDocument.getPage(pageNumber);
      const canvas = document.getElementById(canvasId) as HTMLCanvasElement;

      if (!canvas) {
        return;
      }

      const context = canvas.getContext('2d');
      if (!context) {
        return;
      }

      const pageInfo = pagesInfo.find(p => p.pageNumber === pageNumber);
      if (!pageInfo) {
        return;
      }

      // Get viewport at the desired display scale, respecting page rotation
      const viewport = page.getViewport({ scale: pageInfo.scale, rotation: page.rotate });

      // High-DPI support
      const outputScale = window.devicePixelRatio || 1;

      // Set canvas pixel dimensions (actual rendering resolution)
      canvas.width = Math.floor(viewport.width * outputScale);
      canvas.height = Math.floor(viewport.height * outputScale);

      // Set canvas CSS dimensions (display size)
      canvas.style.width = Math.floor(viewport.width) + 'px';
      canvas.style.height = Math.floor(viewport.height) + 'px';

      // Get a scaled viewport for high-DPI rendering
      // CRITICAL: Create viewport at the FINAL render scale (pageInfo.scale * devicePixelRatio)
      // This makes PDF.js render at the correct resolution without needing transform
      const renderViewport = page.getViewport({
        scale: pageInfo.scale * outputScale,
        rotation: page.rotate
      });

      // Clear canvas before rendering
      context.clearRect(0, 0, canvas.width, canvas.height);

      // Cancel any existing render task for this canvas
      const existingTask = renderTasksRef.current.get(canvasId);
      if (existingTask) {
        try {
          existingTask.cancel();
        } catch (err) {
          // Ignore cancellation errors
        }
        renderTasksRef.current.delete(canvasId);
      }

      // Start new render task and track it
      const renderTask = page.render({
        canvasContext: context,
        viewport: renderViewport,
        transform: null,
      } as any);

      renderTasksRef.current.set(canvasId, renderTask);

      // Render PDF page - viewport already includes device pixel ratio scaling
      await renderTask.promise;

      // Remove completed task from tracking
      renderTasksRef.current.delete(canvasId);

      // Render text layer for text selection and strikeout
      const canvasContainer = canvas.parentElement;
      if (canvasContainer) {
        // Remove existing textLayer if present
        const existingTextLayer = canvasContainer.querySelector('.textLayer');
        if (existingTextLayer) {
          existingTextLayer.remove();
        }

        // Create new textLayer - NO inline width/height (CSS handles via inset: 0)
        const textLayerDiv = document.createElement('div');
        textLayerDiv.className = 'textLayer';

        // PDF.js v5+ requires CSS custom properties for proper text scaling
        // These properties allow CSS-based scaling as per PDF.js PR #20491
        const devicePixelRatio = window.devicePixelRatio || 1;
        textLayerDiv.style.setProperty('--scale-factor', String(viewport.scale));
        textLayerDiv.style.setProperty('--viewport-scale', String(viewport.scale));
        textLayerDiv.style.setProperty('--text-scale-factor', String(viewport.scale));
        textLayerDiv.style.setProperty('--total-scale-factor', String(viewport.scale * devicePixelRatio));

        // Don't set pointer-events or user-select inline - let CSS control via .textLayer and .strikeout-mode classes

        // Get text content and render
        const textContent = await page.getTextContent();
        const textLayer = new pdfjsLib.TextLayer({
          textContentSource: textContent,
          container: textLayerDiv,
          viewport: viewport,
        });
        await textLayer.render();

        // Insert textLayer after canvas
        canvasContainer.appendChild(textLayerDiv);
      }
    } catch (err: any) {
      // Silently handle rendering errors (except cancellations which are expected)
      if (err?.name !== 'RenderingCancelledException') {
        console.error(`Failed to render page ${pageNumber}:`, err);
      }
      // Clean up task tracking on error
      renderTasksRef.current.delete(canvasId);
    }
  }, [pdfDocument, pagesInfo]);

  return {
    pdfDocument,
    pagesInfo,
    totalPages,
    isLoading,
    error,
    renderPage,
  };
};
